import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { usePortalMenu } from "./usePortalMenu";
import {
  createEmailInvite,
  createUserInvite,
  getInviteKey,
  isEmailAlreadyInvited,
  isUserAlreadyInvited,
} from "../services/workspaces/workspaceMembers.helpers";
import type {
  WorkspaceInviteRole,
  WorkspaceMemberInvite,
  WorkspaceUserSearchResult,
} from "../services/workspaces/workspaceMembers.types";
import { isValidEmail } from "../utils/authValidation";
import { MIN_WORKSPACE_USER_SEARCH_LENGTH, useWorkspaceUserSearch } from "./useWorkspaceUserSearch";
import { MIN_TENANT_USER_SEARCH_LENGTH, useTenantUserSearch } from "./useTenantUserSearch";

/** Which user pool the picker searches — the two callers need different ones. */
export type MemberInvitePickerSearchScope = "workspace" | "tenant";

export interface UseMemberInvitePickerOptions {
  invites: WorkspaceMemberInvite[];
  onInvitesChange: (invites: WorkspaceMemberInvite[]) => void;
  disabled?: boolean;
  /**
   * "workspace" (default) searches `/api/v1/workspaces/users` — existing
   * behavior for WorkspaceWizardModal's member step. "tenant" searches
   * `/api/v2/tenants/:tenantId/users` — used by ShareStoryModal, whose
   * invitees can be anyone in the tenant, not just current workspace members.
   */
  searchScope?: MemberInvitePickerSearchScope;
  /** Role new invitees start with; Share starts on viewer, the workspace wizard on editor. */
  initialRole?: WorkspaceInviteRole;
}

export const useMemberInvitePicker = ({
  invites,
  onInvitesChange,
  disabled = false,
  searchScope = "workspace",
  initialRole = "editor",
}: UseMemberInvitePickerOptions) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [defaultRole, setDefaultRole] = useState<WorkspaceInviteRole>(initialRole);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 150);
    return () => window.clearTimeout(timer);
  }, [query]);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  const { triggerRef: anchorRef, menuRef, rect } = usePortalMenu<
    HTMLDivElement,
    HTMLDivElement
  >({
    open: dropdownOpen,
    onOutsideClick: closeDropdown,
  });

  const minSearchLength =
    searchScope === "tenant"
      ? MIN_TENANT_USER_SEARCH_LENGTH
      : MIN_WORKSPACE_USER_SEARCH_LENGTH;
  const shouldSearch =
    dropdownOpen && debouncedQuery.trim().length >= minSearchLength;

  // Both hooks are called unconditionally (Rules of Hooks) but only the one
  // matching `searchScope` is actually enabled, so only it fires a request.
  const workspaceSearch = useWorkspaceUserSearch({
    query: debouncedQuery,
    enabled: shouldSearch && searchScope === "workspace",
  });
  const tenantSearch = useTenantUserSearch({
    query: debouncedQuery,
    enabled: shouldSearch && searchScope === "tenant",
  });
  const { data, isLoading, isFetching } =
    searchScope === "tenant" ? tenantSearch : workspaceSearch;

  const results = data?.items ?? [];
  const searchLoading = isLoading || isFetching;

  const emailInviteCandidate = useMemo(() => {
    const trimmed = query.trim();
    if (!isValidEmail(trimmed)) return null;
    if (isEmailAlreadyInvited(invites, trimmed)) return null;
    const inResults = results.some(
      (user) => user.email.toLowerCase() === trimmed.toLowerCase(),
    );
    if (inResults) return null;
    return trimmed;
  }, [invites, query, results]);

  const openDropdown = useCallback(() => {
    if (!disabled) setDropdownOpen(true);
  }, [disabled]);

  const handleQueryChange = useCallback(
    (next: string) => {
      setQuery(next);
      if (next.trim().length >= minSearchLength) {
        setDropdownOpen(true);
      }
    },
    [minSearchLength],
  );

  const clearSearch = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    closeDropdown();
    inputRef.current?.focus();
  }, [closeDropdown]);

  const addInvite = useCallback(
    (invite: WorkspaceMemberInvite) => {
      onInvitesChange([...invites, invite]);
      clearSearch();
    },
    [clearSearch, invites, onInvitesChange],
  );

  const handleSelectUser = useCallback(
    (user: WorkspaceUserSearchResult) => {
      if (isUserAlreadyInvited(invites, user.id)) {
        clearSearch();
        return;
      }
      if (isEmailAlreadyInvited(invites, user.email)) {
        clearSearch();
        return;
      }
      addInvite(createUserInvite(user, defaultRole));
    },
    [addInvite, clearSearch, defaultRole, invites],
  );

  const handleSelectEmailInvite = useCallback(
    (email: string) => {
      if (isEmailAlreadyInvited(invites, email)) {
        clearSearch();
        return;
      }
      addInvite(createEmailInvite(email, defaultRole));
    },
    [addInvite, clearSearch, defaultRole, invites],
  );

  const handleRoleChange = useCallback(
    (inviteKey: string, role: WorkspaceInviteRole) => {
      onInvitesChange(
        invites.map((invite) =>
          getInviteKey(invite) === inviteKey ? { ...invite, role } : invite,
        ),
      );
    },
    [invites, onInvitesChange],
  );

  const handleRemove = useCallback(
    (inviteKey: string) => {
      onInvitesChange(
        invites.filter((invite) => getInviteKey(invite) !== inviteKey),
      );
    },
    [invites, onInvitesChange],
  );

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        closeDropdown();
        return;
      }
      if (event.key === "Enter" && emailInviteCandidate) {
        event.preventDefault();
        handleSelectEmailInvite(emailInviteCandidate);
      }
    },
    [closeDropdown, emailInviteCandidate, handleSelectEmailInvite],
  );

  return {
    query,
    defaultRole,
    dropdownOpen,
    results,
    searchLoading,
    emailInviteCandidate,
    inputRef,
    anchorRef,
    menuRef,
    menuRect: rect,
    setDefaultRole,
    handleQueryChange,
    openDropdown,
    closeDropdown,
    clearSearch,
    handleSelectUser,
    handleSelectEmailInvite,
    handleRoleChange,
    handleRemove,
    handleInputKeyDown,
  };
};
